import { memo, ReactElement, useCallback, useMemo, useRef } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ExclusivitySetResponse, ActionEntityType } from '@pmp/api';
import { Guard } from '@ohpen/react-authz';
import { useListTableState } from '@pmp/hooks';
import { formatDate, lockActionTitle } from '@pmp/utils';
import { ListProps } from '@pmp/types';
import { useExportTemplatesStore } from '@pmp/stores';

import { exclusiveSetsColumns } from '../tableConfig';
import { useChangeLogAction } from '../../changeLog';

const entityType = ActionEntityType.EXCLUSIVITY_SETS;
const rowKey = 'id';

const toChangeLogRecord = (record: ExclusivitySetResponse) => ({ id: record.id ?? '', name: record.name ?? record.id ?? '' });

export const ExclusiveProductsList = memo(
  ({ isFetching, valueLists, data, onCreate, onView, onEdit, onDelete }: ListProps<ExclusivitySetResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
    const { t } = useTranslation();
    const { changeLogAction, changeLogDrawer } = useChangeLogAction(entityType, toChangeLogRecord);
    const tableState = useListTableState<ExclusivitySetResponse>(entityType, rowKey, tableRef, exclusiveSetsColumns);

    const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.EXCLUSIVITY_SETS), [getExportTemplates]);

    const getRowActions: (record: ExclusivitySetResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('enso.buttons.view')} placement="top">
            <Button type="text" aria-label="View exclusives" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['exclusivitySets:update']} flags={['enableExclusivesCrud']}>
            <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit exclusives"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['exclusivitySets:delete']} flags={['enableExclusivesCrud']}>
            <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete exclusives"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          changeLogAction(record),
        ];

        return rowActions;
      },
      [onDelete, onEdit, onView, t, changeLogAction],
    );

    const tableProps: TableProps<ActionEntityType, ExclusivitySetResponse> = useMemo(
      () => ({
        ...tableState,
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns: exclusiveSetsColumns,
        rowKey,
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        resizableColumns: true,
        valueLists,
        loading: isFetching,
        getRowActions,
        showViews: true,
      }),
      [tableState, data, valueLists, isFetching, getRowActions],
    );

    return (
      <>
        <Table {...tableProps}>
          <Table.ActionsBar>
            <Flex justify="space-between">
              <Space size="middle">
                <Table.GlobalSearch />
              </Space>
              <Space size="middle">
                <Table.ResetFiltersButton />
                <Table.SelectColumnsButton />
                <Guard flags={['enableExports']}>
                  <Table.ExportButton
                    editableFileName
                    fileName={`Exclusive Sets ${formatDate()}`}
                    sheetName="Exclusive Sets"
                    disabled={isFetching}
                    loadingTemplates={isLoadingExportTemplates}
                    getTemplates={getTemplates}
                    modalDefaults={{ columnScope: 'template' }}
                    allowSearch={true}
                    exportView
                  />
                </Guard>
                <Guard permissions={['exclusivitySets:create']} flags={['enableExclusivesCrud']}>
                  <Table.CreateButton onClick={onCreate} />
                </Guard>
              </Space>
            </Flex>
          </Table.ActionsBar>
        </Table>
        {changeLogDrawer}
      </>
    );
  },
);
